import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Youtube, BookOpen, Radio } from 'lucide-react';

function XIcon({ size = 14, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const PLATFORM_ICONS = {
  twitter: XIcon,
  youtube: Youtube,
  substack: BookOpen,
  podcast: Radio,
};

const DIRECTION_CONFIG = {
  bullish: {
    label: 'Bullish',
    icon: TrendingUp,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
  },
  bearish: {
    label: 'Bearish',
    icon: TrendingDown,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500',
  },
  neutral: {
    label: 'Neutral',
    icon: Minus,
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
    dot: 'bg-gray-400',
  },
};

function formatReturn(pct) {
  if (pct === null || pct === undefined) return 'N/A';
  const n = parseFloat(pct);
  if (n === 0) return 'N/A';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const PERIOD_FIELD = {
  current: 'current_return_pct',
  '7d':  'return_at_7d',
  '1m':  'return_at_1m',
  '3m':  'return_at_3m',
  '6m':  'return_at_6m',
  '1y':  'return_at_1y',
};

const PERIOD_LABEL = {
  current: 'Return',
  '7d': '7D Return',
  '1m': '1M Return',
  '3m': '3M Return',
  '6m': '6M Return',
  '1y': '1Y Return',
};

export default function PredictionCard({ prediction, noLink = false, onCardClick, returnPeriod = 'current' }) {
  const dir = DIRECTION_CONFIG[prediction.direction] || DIRECTION_CONFIG.neutral;
  const DirIcon = dir.icon;
  const PlatformIcon = PLATFORM_ICONS[prediction.platform] || null;
  const returnField = PERIOD_FIELD[returnPeriod] ?? 'current_return_pct';
  const returnValue = prediction[returnField];
  const returnPct = parseFloat(returnValue ?? 0);
  const returnColor = returnValue == null
    ? 'text-gray-400'
    : returnPct === 0
    ? 'text-gray-500'
    : returnPct > 0
    ? 'text-emerald-600'
    : 'text-red-600';

  return (
    <Link
      to={`/analysts/predictions/${prediction.id}`}
      onClick={noLink ? (e) => { e.preventDefault(); onCardClick?.(); } : undefined}
      className={`block bg-white rounded-xl border border-gray-100 p-4 transition-all duration-200 ${
        noLink ? 'cursor-pointer hover:border-gray-300 hover:shadow-md' : 'hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* Header row: symbol + direction badge */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-lg font-bold text-gray-900">{prediction.symbol_ticker}</span>
          <span className="ml-2 text-sm text-gray-400">{prediction.symbol_name}</span>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${dir.bg} ${dir.text} ${dir.border}`}
        >
          <DirIcon size={12} />
          {dir.label}
        </span>
      </div>

      {/* Analyst row */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/analysts/${prediction.analyst_slug}`}
            onClick={(e) => { e.stopPropagation(); if (noLink) { e.preventDefault(); onCardClick?.(); } }}
            className={`text-sm font-medium text-gray-800 truncate block ${noLink ? 'cursor-default' : 'hover:text-gray-900'}`}
          >
            {prediction.analyst_name}
          </Link>
        </div>
        {PlatformIcon && (
          <PlatformIcon size={14} className="text-gray-400 flex-shrink-0" />
        )}
      </div>

      {/* Metrics row */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{formatDate(prediction.published_at)}</span>
        <span
          className={`font-semibold text-sm ${returnColor}`}
          title={returnPeriod !== 'current' && returnValue == null ? 'Not enough data for this period' : undefined}
        >
          {returnValue == null && returnPeriod !== 'current'
            ? '—'
            : formatReturn(returnValue)
          }
          {returnPeriod !== 'current' && returnValue != null && (
            <span className="ml-1 text-xs font-normal text-gray-400">{PERIOD_LABEL[returnPeriod]}</span>
          )}
        </span>
      </div>

      {/* Price target if present */}
      {prediction.stated_price_target && (
        <div className="mt-2 pt-2 border-t border-gray-50 text-xs text-gray-400">
          Target: <span className="text-gray-600 font-medium">
            ${parseFloat(prediction.stated_price_target).toFixed(2)}
          </span>
          {prediction.stated_timeframe && (
            <span className="ml-1">{prediction.stated_timeframe}</span>
          )}
        </div>
      )}
    </Link>
  );
}
